
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ProjectModal from './ProjectModal';
import projectsData from '@/data/projects.json';

interface Project {
  id: string
  title: string
  description: string
  category: string
  tech_stack: string[]
  image_url: string | null
  project_url: string | null
  is_featured: boolean
  display_order: number
  is_active: boolean
  problem?: string | null
  solution?: string | null
  impact?: string | null
}

const ProjectsShowcase = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  useEffect(() => {
    // Load static data
    const data = (projectsData as Project[])
      .filter(p => p.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setProjects(data)
    setLoading(false)
  }, []);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  // Handle filter change with animation
  const handleFilterChange = (newFilter: string) => {
    if (newFilter === filter) return;
    
    setIsTransitioning(true);
    
    // Wait for fade out animation
    setTimeout(() => {
      setFilter(newFilter);
      setIsTransitioning(false);
    }, 200);
  };

  // Get unique categories from projects
  const categories = ['All', ...Array.from(new Set(projects.map(project => project.category)))];
  
  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(project => project.category === filter);

  if (loading) {
    return (
      <section className="section-padding bg-goeltec-background">
        <div className="container-custom">
          <div className="text-center">Loading projects...</div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="section-padding bg-goeltec-background">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-foreground font-heading">
              Our Projects
            </h2>
            <div className="h-1 w-20 bg-primary mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-muted-foreground">
              Explore our portfolio of successful projects delivered across various industries.
            </p>
          </div>
          
          {/* Filter Buttons */}
          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12 animate-on-scroll">
              {categories.map((filterItem) => (
                <button
                  key={filterItem}
                  className={`px-6 py-3 rounded-full transition-all duration-300 font-medium ${
                    filter === filterItem 
                      ? 'bg-primary text-primary-foreground shadow-lg scale-105' 
                      : 'bg-white text-foreground hover:bg-accent hover:scale-105 shadow-md'
                  }`}
                  onClick={() => handleFilterChange(filterItem)}
                >
                  {filterItem}
                </button>
              ))}
            </div>
          )}
          
          {/* Projects Grid with Animation */}
          {filteredProjects.length === 0 ? (
            <div className="text-center text-muted-foreground">
              <p>No projects found. Please run the data fetch script to populate content.</p>
            </div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-300 ${
              isTransitioning 
                ? 'opacity-0 scale-95 transform translate-y-4' 
                : 'opacity-100 scale-100 transform translate-y-0'
            }`}>
              {filteredProjects.map((project, index) => (
                <div 
                  key={project.id}
                  className="bg-white rounded-lg overflow-hidden shadow-md animate-on-scroll card-hover group cursor-pointer relative"
                  style={{ 
                    animationDelay: isTransitioning ? '0s' : `${index * 0.1}s`,
                    animationDuration: '0.6s',
                    animationFillMode: 'both'
                  }}
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={project.image_url || "/placeholder.svg"} 
                      alt={project.title} 
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                      {/* Corner Lines */}
                      <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100"></div>
                      <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100"></div>
                      
                      {/* Text Overlay */}
                      <div className="text-center text-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                        <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                        <p className="text-sm">
                          {project.category} × {project.tech_stack[0] || 'Technology'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                        {project.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                    <p className="text-muted-foreground mb-3">{project.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tech_stack.map((tech) => (
                        <span 
                          key={tech} 
                          className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="text-primary font-medium inline-flex items-center hover:underline">
                      View Details
                      <ArrowRight size={16} className="ml-1" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="text-center mt-12">
            <Link to="/projects" className="btn-primary">
              View All Projects
            </Link>
          </div>
        </div>
      </section>
      
      {/* Project Modal */}
      <ProjectModal 
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default ProjectsShowcase;
